import { useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTenantConfig, useCartActions } from '@webmono/storefront-ui/providers'
import type { ModifierGroup, Modifier, SelectedModifier } from '@webmono/storefront-ui/types'
import { formatCents, cn } from '@webmono/storefront-ui/utils'
import { DIETARY_LABELS, calculateItemPrice } from '@webmono/storefront-ui/types'
import { useMenuItem } from '@/lib/hooks'

interface ModifierGroupSectionProps {
  group: ModifierGroup
  selectedModifiers: SelectedModifier[]
  onSelect: (groupId: string, modifier: Modifier, selected: boolean) => void
}

function ModifierGroupSection({ group, selectedModifiers, onSelect }: ModifierGroupSectionProps) {
  const tenant = useTenantConfig()
  const selectedInGroup = selectedModifiers.filter((m) => m.groupId === group.id)
  const selectionCount = selectedInGroup.length

  const isRadio = group.minSelections === 1 && group.maxSelections === 1
  const canSelectMore = selectionCount < group.maxSelections
  const needsMore = selectionCount < group.minSelections

  const getConstraintText = () => {
    if (group.minSelections === group.maxSelections) {
      return `Select exactly ${group.minSelections}`
    }
    if (group.minSelections > 0 && group.maxSelections < group.modifiers.length) {
      return `Select ${group.minSelections}-${group.maxSelections}`
    }
    if (group.minSelections > 0) {
      return `Select at least ${group.minSelections}`
    }
    if (group.maxSelections < group.modifiers.length) {
      return `Select up to ${group.maxSelections}`
    }
    return null
  }

  return (
    <div className="border-b border-border pb-4 last:border-0">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-medium text-foreground">{group.name}</h3>
          {group.description && (
            <p className="text-sm text-muted-foreground">{group.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {getConstraintText() && (
            <span className={cn(
              'text-xs px-2 py-0.5 rounded-full',
              needsMore ? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground'
            )}>
              {getConstraintText()}
            </span>
          )}
          {group.required && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-destructive/10 text-destructive">
              Required
            </span>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {group.modifiers.map((modifier) => {
          const isSelected = selectedInGroup.some((m) => m.modifierId === modifier.id)
          const isDisabled = !modifier.available || (!isSelected && !canSelectMore)

          return (
            <button
              key={modifier.id}
              onClick={() => {
                if (isDisabled) return
                if (isRadio) {
                  selectedInGroup.forEach((m) => onSelect(group.id, m.modifier, false))
                  onSelect(group.id, modifier, true)
                } else {
                  onSelect(group.id, modifier, !isSelected)
                }
              }}
              disabled={isDisabled}
              className={cn(
                'w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left',
                isSelected ? 'bg-primary/10 border-2 border-primary' : 'bg-card border-2 border-transparent',
                isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted/50'
              )}
            >
              <div className={cn(
                'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                isSelected ? 'border-primary bg-primary' : 'border-muted-foreground'
              )}>
                {isSelected && (
                  <svg className="w-3 h-3 text-primary-foreground" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <span className="font-medium text-foreground">{modifier.name}</span>
                {!modifier.available && (
                  <span className="ml-2 text-xs text-destructive">Unavailable</span>
                )}
              </div>
              {modifier.priceDeltaCents !== 0 && (
                <span className={cn(
                  'text-sm font-mono',
                  modifier.priceDeltaCents > 0 ? 'text-muted-foreground' : 'text-success'
                )}>
                  {modifier.priceDeltaCents > 0 ? '+' : ''}
                  {formatCents(modifier.priceDeltaCents, tenant.operations.currency)}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export function ItemDetailPage() {
  const { itemId } = useParams<{ itemId: string }>()
  const navigate = useNavigate()
  const tenant = useTenantConfig()
  const { addItem } = useCartActions()
  const { data: item, isLoading } = useMenuItem(itemId!)

  const [selectedModifiers, setSelectedModifiers] = useState<SelectedModifier[]>([])
  const [quantity, setQuantity] = useState(1)
  const [specialInstructions, setSpecialInstructions] = useState('')

  const handleModifierSelect = (groupId: string, modifier: Modifier, selected: boolean) => {
    setSelectedModifiers((prev) => {
      if (selected) {
        return [...prev, { groupId, modifierId: modifier.id, modifier }]
      }
      return prev.filter((m) => !(m.groupId === groupId && m.modifierId === modifier.id))
    })
  }

  const isValid = useMemo(() => {
    if (!item) return false

    return item.modifierGroups.every((group) => {
      const selectedInGroup = selectedModifiers.filter((m) => m.groupId === group.id)
      return selectedInGroup.length >= group.minSelections && selectedInGroup.length <= group.maxSelections
    })
  }, [item, selectedModifiers])

  const totalPrice = useMemo(() => {
    if (!item) return 0
    return calculateItemPrice(item, selectedModifiers) * quantity
  }, [item, selectedModifiers, quantity])

  const handleAddToCart = () => {
    if (!item || !isValid) return
    addItem(item, selectedModifiers, quantity, specialInstructions || undefined)
    navigate('/menu')
  }

  if (isLoading || !item) {
    return (
      <div className="max-w-lg mx-auto px-4 py-6">
        <div className="animate-pulse space-y-4">
          <div className="h-48 bg-muted rounded-lg" />
          <div className="h-6 bg-muted rounded w-2/3" />
          <div className="h-4 bg-muted rounded w-full" />
          <div className="h-4 bg-muted rounded w-3/4" />
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-lg mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="sticky top-[60px] left-4 z-20 m-4 p-2 rounded-full bg-card/80 backdrop-blur-sm shadow-sm border border-border"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      {item.image && (
        <img
          src={item.image}
          alt={item.name}
          className="w-full h-48 object-cover -mt-14"
        />
      )}

      <div className="px-4 py-4">
        <div className="flex items-start justify-between gap-4">
          <h1 className="font-heading text-2xl font-semibold text-foreground">{item.name}</h1>
          <span className="font-mono text-lg font-medium text-foreground">
            {formatCents(item.priceCents, tenant.operations.currency)}
          </span>
        </div>

        <p className="text-muted-foreground mt-2">{item.description}</p>

        <div className="flex flex-wrap gap-2 mt-3">
          {item.dietaryTags.map((tag) => (
            <span
              key={tag}
              className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground"
            >
              {DIETARY_LABELS[tag]?.label}
            </span>
          ))}
        </div>

        {!item.available && (
          <div className="mt-4 p-3 rounded-lg bg-destructive/10 text-destructive text-center font-medium">
            This item is currently sold out
          </div>
        )}

        {item.available && item.modifierGroups.length > 0 && (
          <div className="mt-6 space-y-4">
            {item.modifierGroups.map((group) => (
              <ModifierGroupSection
                key={group.id}
                group={group}
                selectedModifiers={selectedModifiers}
                onSelect={handleModifierSelect}
              />
            ))}
          </div>
        )}

        {item.available && tenant.features.specialInstructions && (
          <div className="mt-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              Special Instructions
            </label>
            <textarea
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              placeholder="Any special requests? (allergies, preferences, etc.)"
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>
        )}
      </div>

      {item.available && (
        <div className="sticky bottom-0 p-4 bg-card border-t border-border safe-bottom">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3 bg-muted rounded-full p-1">
              <button
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                className="w-8 h-8 rounded-full bg-card flex items-center justify-center text-foreground hover:bg-muted transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              <span className="font-medium text-foreground w-6 text-center">{quantity}</span>
              <button
                onClick={() => setQuantity((q) => q + 1)}
                className="w-8 h-8 rounded-full bg-card flex items-center justify-center text-foreground hover:bg-muted transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>

            <button
              onClick={handleAddToCart}
              disabled={!isValid}
              className={cn(
                'flex-1 py-3 rounded-full font-medium text-center transition-all',
                isValid
                  ? 'bg-primary text-primary-foreground hover:opacity-90 active:scale-[0.98]'
                  : 'bg-muted text-muted-foreground cursor-not-allowed'
              )}
            >
              Add to Cart · {formatCents(totalPrice, tenant.operations.currency)}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
